import React, { useState, useRef, useMemo } from 'react'
import { compose } from 'recompose'
import { List, Map } from 'immutable'
import { connect as connectRedux } from 'react-redux'
import _isFunction from 'lodash/isFunction'
import _get from 'lodash/get'
import ReactPlayerLoader from '@brightcove/react-player-loader'
import { getBoundActions } from '../../actions/index'
import { get as getConfig } from '../../config/index'
import { getPrimary } from '../../services/languages/index'
// import { createAbsolute } from '../../services/url/index'
// import { copyToClipboard } from '../../services/share/index'
import { getAuthIsLoggedIn } from '../../services/auth/index'
import {
  // SHORT_SHARED_GA4_EVENT,
  SHORT_70_COMPLETE_GA4_EVENT,
  SHORT_STARTED_GA4_EVENT,
  CLICK_TO_DETAILS_PAGE_GA4_EVENT,
} from '../../services/event-tracking/index'
import {
  CONTENT_IMAGE_TYPE_KEYART_WITH_TEXT,
  CONTENT_IMAGE_TYPE_KEYART_NO_TEXT,
} from '../../services/content/index'
import Button, { TYPES } from '../Button/index'
// import ShareIcon from '../Icon/icons/Share/index'
import CommentIcon from '../Icon/icons/Comment/index'
import PlayOutlineIcon from '../Icon/icons/PlayOutline/PlayOutline'
import PlayIcon from '../Icon/icons/Play/Play'
import PauseIcon from '../Icon/icons/Pause/Pause'
import PlusIcon from '../Icon/icons/Plus/Plus'
import SpeakerMuteIcon from '../Icon/icons/SpeakerMute/SpeakerMute'
import SpeakerFullIcon from '../Icon/icons/SpeakerFull/SpeakerFull'
import ButtonSignUp, { BUTTON_SIGN_UP_TYPE_BUTTON } from '../ButtonSignUp/index'
import { getMetaImageUrls } from '../DetailPageV3/utils'
import Picture, { IMAGE_TYPE_WEBP, IMAGE_LOADING_LAZY } from '../Picture/index'
import PlaylistAddRemove from '../PlaylistAddRemove/index'
import {
  PLUGIN_NAME as REMAINING_TIME_COUNTDOWN_PLUGIN,
  register as registerRemainingTimeCountdown,
} from './video-player-countdown-timer'
import { GA_CLIP_CONTENT_TYPE } from './utils'

const config = getConfig()
const accountId = _get(config, 'brightcove.account')
// const playerId = _get(config, 'brightcove.vertical')
const playerId = _get(config, 'brightcove.player')

/**
 * Vertical Video Player
 * @param {Object} props
 * @param {Immutable.Map} props.auth
 * @param {Immutable.Map} props.video
 * @param {number} props.index
 * @param {number} props.currentVideoIndex
 * @param {Function} props.onCreated
 * @param {Function} props.onDestroyed
 */
function VideoShortsPlayer (props) {
  const {
    auth,
    video,
    index,
    currentVideoIndex,
    onCreated,
    onDestroyed,
    // resolver,
    staticText,
    // addToasty,
    userLanguage,
    viewportWidth,
    onFeedbackClick,
    setEventDefault,
  } = props

  const isLoggedIn = getAuthIsLoggedIn(auth)
  const isMobile = viewportWidth < 768
  const iconSize = isMobile ? 28 : 52

  const playerRef = useRef(null)
  const completeEventSent = useRef(false)

  const [metaVisible, setMetaVisible] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isMuted, setIsMuted] = useState(false)

  const metaClassName = [
    'vertical-videos-page__video-player-meta',
    metaVisible ? 'vertical-videos-page__video-player-meta--visible' : null,
  ].filter(Boolean).join(' ')

  const isCurrentVideo = index === currentVideoIndex
  const isNextVideo = index + 1 === currentVideoIndex
  const isPreviousVideo = index - 1 === currentVideoIndex

  const shouldRenderPlayer = isCurrentVideo || isNextVideo || isPreviousVideo

  const contentId = video.get('contentId')
  const hlsUrl = video.getIn(['featureMedia', 'mediaAssets', 0, 'videoPlayback', 'hlsUrl'])
  const bcId = video.getIn(['featureMedia', 'mediaAssets', 0, 'providerKey'])
  const parentContent = video.getIn(['contentReferencesFrom', 0, 'contentTo', 0])
  const images = parentContent.get('imageContentRelationships', List()) || List()
  const imageData = images.find((img) => img.get('type') === CONTENT_IMAGE_TYPE_KEYART_WITH_TEXT) || Map()
  const clipImageData = images.find((img) => img.get('type') === CONTENT_IMAGE_TYPE_KEYART_NO_TEXT) || Map()
  const { fallbackImageUrl, imageUrl } = getMetaImageUrls(imageData.getIn(['image', 'url'], ''), 144, 96)
  const {
    fallbackImageUrl: clipFallbackImageUrl,
    imageUrl: clipImageUrl,
  } = getMetaImageUrls(clipImageData.getIn(['image', 'url'], ''), 385, 539)

  const vjsOpts = {
    language: userLanguage,
    loop: isLoggedIn,
    playsinline: true,
    poster: clipImageUrl || clipFallbackImageUrl,
  }

  const handleSuccess = (data) => {
    const player = data.ref
    player.ready(() => {
      playerRef.current = player
      player.src({
        src: hlsUrl,
        type: 'application/x-mpegURL',
      })
      player.preload('auto')
      const registeredPlugins = global.videojs.getPlugins
        ? global.videojs.getPlugins()
        : {}
      if (registeredPlugins[REMAINING_TIME_COUNTDOWN_PLUGIN] === undefined) {
        registerRemainingTimeCountdown(global.videojs)
      }
      if (_isFunction(player.RemainingTimeCountdown)) {
        player.RemainingTimeCountdown({
          ariaLabel: staticText.get('remainingTimeCountdown'),
        })
      }
      player.on('useractive', () => {
        setMetaVisible(true)
      })
      player.on('userinactive', () => {
        setMetaVisible(false)
      })
      player.on('pause', () => {
        setIsPlaying(false)
      })
      player.on('play', () => {
        setMetaVisible(true)
        setIsPlaying(true)
      })
      player.one('play', () => {
        setEventDefault(SHORT_STARTED_GA4_EVENT.mergeDeep({
          bc_id: bcId,
          content_id: contentId,
          content_type: GA_CLIP_CONTENT_TYPE,
          ui_location: 'shorts',
        }))
      })
      player.on('timeupdate', () => {
        if (player.currentTime() >= player.duration() * 0.7 && !completeEventSent.current) {
          setEventDefault(SHORT_70_COMPLETE_GA4_EVENT.mergeDeep({
            bc_id: bcId,
            content_id: contentId,
            content_type: GA_CLIP_CONTENT_TYPE,
            ui_location: 'shorts',
          }))
          completeEventSent.current = true
        }
      })
      player.on('dispose', () => {
        playerRef.current = null
        if (_isFunction(onDestroyed)) {
          onDestroyed()
        }
      })
    })
    if (_isFunction(onCreated)) {
      onCreated(player)
    }
  }

  const handleFailure = () => {
    playerRef.current = null
    if (_isFunction(onDestroyed)) {
      onDestroyed()
    }
  }

  const handlePlayClick = () => {
    if (playerRef.current) {
      if (playerRef.current.paused()) {
        playerRef.current.play()
      } else {
        playerRef.current.pause()
      }
    }
  }

  const handleVolumeClick = () => {
    if (playerRef.current) {
      playerRef.current.muted(!playerRef.current.muted())
      setIsMuted(!isMuted)
    }
  }

  // TODO: Add share button back in when we fix content path
  // const handleCopyUrlClick = () => {
  //   const url = resolver.get('path')

  //   const absoluteUrl = createAbsolute(url)
  //   copyToClipboard(absoluteUrl)
  //   addToasty(staticText.get('copied'))

  //   setEventDefault(SHORT_SHARED_GA4_EVENT.mergeDeep({
  //     bc_id: bcId,
  //     content_id: contentId,
  //     content_type: GA_CLIP_CONTENT_TYPE,
  //     ui_location: 'shorts',
  //   }))
  // }

  const handleFeedbackClick = () => {
    if (_isFunction(onFeedbackClick)) {
      if (playerRef.current) {
        playerRef.current.pause()
      }
      onFeedbackClick()
    }
  }
  const watchButton = useMemo(() => {
    if (parentContent.getIn(['featureMedia', 'contentPolicy', 'accessCheck', 'allowed'])) {
      return <Button
        type={TYPES.ICON}
        iconSvg={<PlayOutlineIcon size={20}/>}
        url={`${parentContent.get('path')}?fullplayer=feature`}
        className="vertical-videos-page__video-player-watch-button"
        onClick={() => {
          setEventDefault(CLICK_TO_DETAILS_PAGE_GA4_EVENT.mergeDeep({
            content_name: parentContent.get('title'),
            content_id: parentContent.get('contentId'),
            content_type: parentContent.get('contentType'),
            media_id: parentContent.getIn(['featureMedia', 'mediaId']),
            media_language: parentContent.get('languageFullText'),
            is_featured_media: true,
            ui_location: 'shorts',
          }))
        }}
      >
        <span className="vertical-videos-page__video-player-watch-button-label">
          {staticText.get('watchFullVideo')}
        </span>
      </Button>
    }

    return <ButtonSignUp
      buttonClass="vertical-videos-page__video-player-watch-button"
      text={isLoggedIn ? staticText.get('upgradeToWatch') : staticText.get('signUpToWatch')}
      type={BUTTON_SIGN_UP_TYPE_BUTTON}
      scrollToTop
    />
  }, [isLoggedIn, parentContent, staticText])

  if (!video) {
    return null
  }

  return (
    <div
      className="vertical-videos-page__video-player"
      style={{ backgroundImage: `url(${clipImageUrl || clipFallbackImageUrl})` }}
    >
      <div className={metaClassName}>
        {video.get('title') && (
          <p className="vertical-videos-page__video-player-meta-title">
            {video.get('title')}
          </p>
        )}
        {video.get('shortDescription') && (
          <p className="vertical-videos-page__video-player-meta-description">
            {video.get('shortDescription')}
          </p>
        )}
        <div className="vertical-videos-page__video-player-meta-parent-content">
          {!!imageUrl && !!fallbackImageUrl && (
            <div className="vertical-videos-page__video-player-meta-parent-image">
              <Picture
                fallbackSrc={fallbackImageUrl}
                width={'144'}
                height={'96'}
                alt={parentContent.get('title')}
                loading={IMAGE_LOADING_LAZY}
                sources={[
                  {
                    type: IMAGE_TYPE_WEBP,
                    srcSet: imageUrl,
                  },
                ]}
              />
            </div>
          )}
          <div className="vertical-videos-page__video-player-meta-parent-title">
            <p>{parentContent.get('title')}</p>
            {watchButton}
          </div>
        </div>
      </div>
      <div className="vertical-videos-page__video-player-controls">
        <Button
          type={TYPES.ICON}
          iconSvg={isPlaying
            ? <PauseIcon size={iconSize}/>
            : <PlayIcon size={iconSize}/>
          }
          onClick={handlePlayClick}
          className="vertical-videos-page__video-player-controls-button"
        />
        <Button
          type={TYPES.ICON}
          iconSvg={isMuted
            ? <SpeakerMuteIcon size={iconSize}/>
            : <SpeakerFullIcon size={iconSize}/>
          }
          onClick={handleVolumeClick}
          className="vertical-videos-page__video-player-controls-button"
        />
      </div>
      <div className="vertical-videos-page__video-player-actions">
        {isLoggedIn && (
          <PlaylistAddRemove
            buttonSvgIcon={<PlusIcon size={28}/>}
            buttonLabel={staticText.get('playlist')}
            className="vertical-videos-page__video-player-actions-button"
            labelClassName="vertical-videos-page__video-player-actions-button-label"
            contentId={parentContent.get('contentId')}
            contentTitle={parentContent.get('title')}
            contentType={parentContent.get('contentType')}
            ga4EventData={{
              content_name: parentContent.get('title'),
              content_id: parentContent.get('contentId'),
              content_type: parentContent.get('contentType'),
              media_id: parentContent.getIn(['featureMedia', 'mediaId']),
              media_language: parentContent.get('languageFullText'),
              is_featured_media: true,
              ui_location: 'shorts',
            }}
          />
        )}
        {/* TODO: Add share button back in when we fix content path */}
        {/* <Button
          type={TYPES.ICON}
          iconSvg={<ShareIcon size={28}/>}
          onClick={handleCopyUrlClick}
          className="vertical-videos-page__video-player-actions-button"
        >
          <span className="vertical-videos-page__video-player-actions-button-label">
            {staticText.get('shareLink')}
          </span>
        </Button> */}
        {isLoggedIn && (
          <Button
            type={TYPES.ICON}
            iconSvg={<CommentIcon size={28}/>}
            onClick={handleFeedbackClick}
            className="vertical-videos-page__video-player-actions-button"
          >
            <span className="vertical-videos-page__video-player-actions-button-label">
              {staticText.get('feedback')}
            </span>
          </Button>
        )}
      </div>
      {shouldRenderPlayer && <ReactPlayerLoader
          onSuccess={handleSuccess}
          onFailure={handleFailure}
          accountId={accountId}
          playerId={playerId}
          options={vjsOpts}
        />
      }
    </div>
  )
}

export default compose(
  connectRedux(
    (state) => {
      return {
        auth: state.auth,
        resolver: state.resolver,
        userLanguage: getPrimary(state.user.getIn(['data', 'language'], List())),
        viewportWidth: state.app.getIn(['viewport', 'width']),
        staticText: state.staticText.getIn(['data', 'verticalVideosPage', 'data'], Map()),
      }
    },
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        addToasty: actions.toasty.addToasty,
        setEventDefault: actions.eventTracking.setEventDefault,
      }
    },
  ),
)(VideoShortsPlayer)
